/**
 * §7.2 — TanStack Query key conventions.
 *
 * `workspaceId` is FIRST in every key. That single rule is what makes
 * logout and workspace-switch invalidation a one-line prefix removal, and it is
 * defence-in-depth alongside backend RLS against cross-workspace data bleed
 * (AC-65). A key shaped `['studies', workspaceId]` would leave one module's cache
 * live after a switch, which is precisely the bug the AC tests for.
 */

export type QueryKey = readonly unknown[]

export const queryKeys = {
  /** Everything under a workspace — the prefix used for invalidation. */
  workspace: (workspaceId: string) => [workspaceId] as const,

  studies: (workspaceId: string, params?: Record<string, unknown>) =>
    [workspaceId, 'studies', 'list', params ?? {}] as const,
  study: (workspaceId: string, studyId: string) =>
    [workspaceId, 'studies', 'detail', studyId] as const,

  briefs: (workspaceId: string, params?: Record<string, unknown>) =>
    [workspaceId, 'briefs', 'list', params ?? {}] as const,
  brief: (workspaceId: string, briefId: string) =>
    [workspaceId, 'briefs', 'detail', briefId] as const,
  briefDraft: (workspaceId: string, briefId: string) =>
    [workspaceId, 'briefs', 'draft', briefId] as const,

  /**
   * MOD-01 sub-resources.
   *
   * All nested under `[workspaceId, 'briefs', <kind>, briefId]` so the single
   * prefix removal on logout or workspace switch takes the TOSCA record, the
   * issue tree, the assumptions and the client-data list with it. Keyed on
   * `briefId` alone — e.g. `['tosca', briefId]` — they would survive a workspace
   * switch, and a brief id is not a secret, which is precisely the AC-65 bug.
   */
  briefTosca: (workspaceId: string, briefId: string) =>
    [workspaceId, 'briefs', 'tosca', briefId] as const,
  briefStructure: (workspaceId: string, briefId: string) =>
    [workspaceId, 'briefs', 'structure', briefId] as const,
  briefAssumptions: (workspaceId: string, briefId: string) =>
    [workspaceId, 'briefs', 'assumptions', briefId] as const,
  briefScenarios: (workspaceId: string, briefId: string) =>
    [workspaceId, 'briefs', 'scenarios', briefId] as const,
  briefDataSources: (workspaceId: string, briefId: string) =>
    [workspaceId, 'briefs', 'data-sources', briefId] as const,
  briefDataQuality: (workspaceId: string, briefId: string) =>
    [workspaceId, 'briefs', 'data-quality', briefId] as const,
  briefErdd: (workspaceId: string, briefId: string) =>
    [workspaceId, 'briefs', 'erdd', briefId] as const,
  briefClientData: (workspaceId: string, briefId: string) =>
    [workspaceId, 'briefs', 'client-data', briefId] as const,
  briefProvenance: (workspaceId: string, briefId: string) =>
    [workspaceId, 'briefs', 'provenance', briefId] as const,
  briefDiff: (workspaceId: string, briefId: string) =>
    [workspaceId, 'briefs', 'diff', briefId] as const,

  /**
   * Reference data, NOT workspace-scoped — and that is a deliberate exception to
   * the rule above, not an oversight. The geography tree, the methodology
   * catalogue, the pricing drivers and the validation rule set are identical for
   * every workspace, so scoping them per workspace would refetch ~4,000 city
   * records on every switch to produce byte-identical data. `me()` is the same
   * exception for the same reason.
   */
  briefTemplates: () => ['reference', 'brief-templates'] as const,
  geography: () => ['reference', 'geography'] as const,
  methodologies: () => ['reference', 'methodologies'] as const,
  estimateDrivers: () => ['reference', 'estimate-drivers'] as const,
  validationRules: () => ['reference', 'validation-rules'] as const,
  clientDataCatalogue: () => ['reference', 'client-data-catalogue'] as const,
  toscaCatalogue: () => ['reference', 'tosca-catalogue'] as const,
  copilot: () => ['reference', 'copilot'] as const,

  knowledgeFeed: (workspaceId: string, studyId: string, params?: Record<string, unknown>) =>
    [workspaceId, 'secondary', 'feed', studyId, params ?? {}] as const,

  kgSearch: (workspaceId: string, query: string) =>
    [workspaceId, 'kg', 'search', query] as const,

  reviewQueue: (workspaceId: string) => [workspaceId, 'review', 'queue'] as const,

  /** Not workspace-scoped: the signed-in identity spans workspaces. */
  me: () => ['me'] as const,
} as const

/**
 * `staleTime` mirrors the backend Redis TTLs from NFR-PE-002. Diverging here
 * produces the worst kind of bug: the client believes data is fresh that the
 * server has already expired, so two users see different numbers and neither is
 * wrong from their own cache's point of view.
 */
export const staleTimes = {
  /** Studies list — backend Redis TTL 30s. */
  studies: 30_000,
  /** Knowledge-graph search — backend Redis TTL 5min. */
  kgSearch: 300_000,
  /** Identity: refetched on focus rather than aggressively cached. */
  me: 60_000,
  /** Live fieldwork figures come from SSE; polling would double-count. */
  liveFieldwork: 0,
  default: 15_000,
} as const
